/**
 * Auth-failure classifier (IM-2017).
 *
 * The Immersive session bootstrap can fail at two points:
 *   1. Auth0 silent-token acquisition (getAccessTokenSilently), or
 *   2. the backend exchange POST /public/auth0_authenticate.
 *
 * Before this, both failures were swallowed into a bare 'no-session' and the
 * app bounced to login with no reason — the beta CORS-redirect outage
 * (REACT_APP_AUTH_DOMAIN pointing at a host that 308-redirects the auth
 * endpoint; browsers can't follow a redirect on a CORS preflight) was invisible
 * from the UI and took a network-tab dig to find.
 *
 * This module turns any thrown error into a structured, human-readable summary
 * so the FE can log it and show it. It is intentionally React-free and
 * dependency-free (no axios import) so it is trivially unit-testable and safe to
 * call from anywhere in the auth path.
 */

export type AuthFailureStep = 'silent-token' | 'exchange' | 'unknown';

export type AuthFailureKind =
  // No HTTP response reached the app: network down, CORS block, or a redirect on
  // a CORS preflight (the classic misconfigured-auth-host case).
  | 'network-or-cors'
  // The server answered with a non-2xx status.
  | 'http-status'
  // The exchange succeeded but returned no permissions.
  | 'no-permissions'
  // Auth0 silent-token step reported no active session (login_required etc.).
  | 'auth0-no-session'
  | 'unknown';

export interface AuthFailureInfo {
  step: AuthFailureStep;
  kind: AuthFailureKind;
  /** Host the failing call targeted, when recoverable (e.g. 'prodapi.immersive-finance.com'). */
  host: string | null;
  /** HTTP status when a response was received. */
  status: number | null;
  /** Low-level error code when present (e.g. 'ERR_NETWORK', 'login_required'). */
  code: string | null;
  /** One-line, user-facing summary. */
  message: string;
}

const hostOf = (url: unknown): string | null => {
  if (typeof url !== 'string' || !url) return null;
  try {
    return new URL(url).host;
  } catch {
    // Relative URL or malformed — surface the raw string trimmed of any path.
    return url.replace(/^([a-z]+:\/\/)?([^/]+).*$/i, '$2') || null;
  }
};

// Auth0 rejections carry an `error` string like 'login_required' /
// 'consent_required' / 'missing_refresh_token'.
const AUTH0_NO_SESSION = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
]);

/**
 * Classify a thrown auth error. `step` is the caller's best knowledge of where
 * it threw; the classifier refines `kind`/`message` from the error shape.
 */
export const describeAuthFailure = (
  error: unknown,
  step: AuthFailureStep = 'unknown',
): AuthFailureInfo => {
  const e = (error ?? {}) as Record<string, any>;

  // Sentinel the exchange caller throws when the POST resolved 200 but carried
  // no permissions.
  if (e.kind === 'no-permissions') {
    return {
      step: 'exchange',
      kind: 'no-permissions',
      host: hostOf(e.host),
      status: 200,
      code: null,
      message: 'Signed in, but your account has no permissions for this app.',
    };
  }

  // Auth0 silent-token failure: no active SSO session to mint a token from.
  const auth0Code = typeof e.error === 'string' ? e.error : null;
  if (auth0Code && AUTH0_NO_SESSION.has(auth0Code)) {
    return {
      step: 'silent-token',
      kind: 'auth0-no-session',
      host: null,
      status: null,
      code: auth0Code,
      message: `Auth0 has no active session (${auth0Code}) — sign in again.`,
    };
  }

  // Axios-shaped error with an HTTP response.
  const response = e.response as Record<string, any> | undefined;
  const configUrl = e.config?.url ?? e.request?.responseURL;
  const host = hostOf(configUrl);

  if (response && typeof response.status === 'number') {
    return {
      step: step === 'unknown' ? 'exchange' : step,
      kind: 'http-status',
      host,
      status: response.status,
      code: e.code ?? null,
      message: `Auth exchange rejected: HTTP ${response.status}${host ? ` from ${host}` : ''}.`,
    };
  }

  // Axios error with NO response: network failure OR a CORS block (including a
  // redirect on the preflight, which the browser refuses to follow).
  const code = typeof e.code === 'string' ? e.code : null;
  if (code === 'ERR_NETWORK' || e.request || e.isAxiosError) {
    return {
      step: step === 'unknown' ? 'exchange' : step,
      kind: 'network-or-cors',
      host,
      status: null,
      code: code ?? 'ERR_NETWORK',
      message: `Auth exchange failed to reach ${host ?? 'the auth host'} (network/CORS — the auth host may be redirecting or blocking this origin).`,
    };
  }

  // Fallback.
  return {
    step,
    kind: 'unknown',
    host,
    status: null,
    code: code,
    message:
      typeof e.message === 'string' && e.message
        ? `Auth failed: ${e.message}`
        : 'Auth failed for an unknown reason.',
  };
};

const STORAGE_KEY = 'immersive:lastAuthError';

/**
 * Persist the failure so it survives the bounce to Auth0 (the app unmounts on
 * logout) and can be read back / shown on return. Best-effort; never throws.
 */
export const recordAuthFailure = (info: AuthFailureInfo): void => {
  // Always log — this is the line a developer sees in the console first.
  // eslint-disable-next-line no-console
  console.error('[auth] session bootstrap failed', info);
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(info));
  } catch {
    // storage disabled / full — the console line above still stands.
  }
};

export const readLastAuthFailure = (): AuthFailureInfo | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthFailureInfo) : null;
  } catch {
    return null;
  }
};

export const clearAuthFailure = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
};
